const SECURITY_CONFIG = {
  allowedProtocols: ['https:', 'blob:'],
  allowedContentTypes: ['application/pdf'],
  // 5MB limit to prevent memory exhaustion
  maxFileSizeBytes: 15 * 1024 * 1024,
  // 2 minutes timeout to prevent hanging requests
  timeoutMs: 120 * 1000,
} as const;

interface DownloadResult {
  success: boolean;
  error?: string;
}

/**
 * Validates and sanitizes a URL to prevent XSS and SSRF attacks
 */
const validateUrl = (url: string): boolean => {
  try {
    const urlObj = new URL(url);
    return (SECURITY_CONFIG.allowedProtocols as readonly string[]).includes(urlObj.protocol);
  } catch {
    return false;
  }
};

/**
 * Sanitizes filename to prevent path traversal and XSS attacks
 */
const sanitizeFilename = (filename: string): string => {
  const sanitized = filename
    .replace(/[<>:"/\\|?*\x00-\x1f]/g, '_') // Replace dangerous characters
    .replace(/\.\./g, '_') // Prevent path traversal
    .replace(/^\.+/, '_') // Prevent hidden files
    .trim();

  if (!sanitized || sanitized.length === 0) {
    return 'download.pdf';
  }

  return sanitized.substring(0, 100);
};

const createTimeoutController = (timeoutMs: number): AbortController => {
  const controller = new AbortController();
  setTimeout(() => controller.abort(), timeoutMs);
  return controller;
};

const validateResponse = async (
  response: Response,
): Promise<{ valid: boolean; error?: string }> => {
  if (!response.ok) {
    return { valid: false, error: `HTTP ${response.status}: ${response.statusText}` };
  }

  const contentType = response.headers.get('content-type');
  if (
    !contentType ||
    !SECURITY_CONFIG.allowedContentTypes.some(type => contentType.includes(type))
  ) {
    return {
      valid: false,
      error: `Invalid file type. Expected PDF, got: ${contentType || 'unknown'}`,
    };
  }

  const contentLength = response.headers.get('content-length');
  if (contentLength && parseInt(contentLength) > SECURITY_CONFIG.maxFileSizeBytes) {
    return {
      valid: false,
      error: `File too large. Maximum size: ${SECURITY_CONFIG.maxFileSizeBytes / (1024 * 1024)}MB`,
    };
  }

  return { valid: true };
};

export const downloadFile = async (url: string, filename: string): Promise<DownloadResult> => {
  let blobUrl: string | null = null;
  let link: HTMLAnchorElement | null = null;

  try {
    if (!validateUrl(url)) {
      return {
        success: false,
        error: 'Invalid URL. Only HTTPS and blob URLs are allowed.',
      };
    }

    const safeFilename = sanitizeFilename(filename);

    const controller = createTimeoutController(SECURITY_CONFIG.timeoutMs);
    const response = await fetch(url, {
      signal: controller.signal,
      mode: 'cors',
      credentials: 'omit',
    });

    const validation = await validateResponse(response);
    if (!validation.valid) {
      return { success: false, error: validation.error };
    }

    const blob = await response.blob();
    if (blob.size > SECURITY_CONFIG.maxFileSizeBytes) {
      return {
        success: false,
        error: `File too large: ${Math.round(blob.size / (1024 * 1024))}MB`,
      };
    }
    blobUrl = window.URL.createObjectURL(blob);
    link = document.createElement('a');
    link.href = blobUrl;
    link.download = safeFilename;
    link.style.display = 'none';

    document.body.appendChild(link);
    link.click();

    return { success: true };
  } catch (error: any) {
    console.error('Secure download failed:', error);

    if (error.name === 'AbortError') {
      return { success: false, error: 'Download timed out. Please try again.' };
    }
    if (error.name === 'NetworkError') {
      return { success: false, error: 'Network error. Please check your connection.' };
    }

    return { success: false, error: 'Download failed. Please try again.' };
  } finally {
    // Let the browser consume the object URL before cleaning it up. Revoking it
    // in the same task as link.click() can cancel repeated downloads in Chrome.
    if (blobUrl || link) {
      const objectUrl = blobUrl;
      const downloadLink = link;

      window.setTimeout(() => {
        if (objectUrl) {
          window.URL.revokeObjectURL(objectUrl);
        }
        if (downloadLink?.parentNode) {
          downloadLink.parentNode.removeChild(downloadLink);
        }
      }, 1000);
    }
  }
};
